import type { ReactNode } from "react";
import { cn } from "../../lib/utils.ts";

export interface AvatarProps {
  /** Display label — first character is used when no glyph/image is given. */
  label?: string;
  /** Emoji or custom glyph shown instead of the initial. */
  glyph?: ReactNode;
  imageUrl?: string;
  /** Pixel diameter. */
  size?: number;
  /** Draw a gold focus ring (e.g. "this is you"). */
  ring?: boolean;
  /** Ghost the avatar behind a blur (private / hidden presence). */
  ghosted?: boolean;
  className?: string;
}

/** Warm gold gradient avatar with optional ring + ghosted privacy state. */
export function Avatar({
  label,
  glyph,
  imageUrl,
  size = 44,
  ring,
  ghosted,
  className,
}: AvatarProps) {
  return (
    <div
      className={cn(
        "grid shrink-0 place-items-center overflow-hidden rounded-full font-display font-semibold",
        className,
      )}
      style={{
        width: size,
        height: size,
        fontSize: size * 0.42,
        color: "oklch(28% 0.05 60)",
        background: imageUrl
          ? undefined
          : "linear-gradient(150deg, var(--gold-soft), var(--gold-deep))",
        boxShadow: ring
          ? "0 0 0 2px var(--charcoal-900), 0 0 0 3.5px var(--gold-500)"
          : "inset 0 1px 0 oklch(100% 0 0 / 40%)",
        filter: ghosted ? "blur(5px) grayscale(0.4)" : undefined,
        opacity: ghosted ? 0.6 : 1,
      }}
      aria-hidden={glyph || imageUrl ? undefined : true}
    >
      {imageUrl ? (
        <img src={imageUrl} alt="" className="size-full object-cover" />
      ) : (
        (glyph ?? (label ? label[0] : ""))
      )}
    </div>
  );
}
